import React, { useMemo, useState, useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";
import { getYahooUrl } from "@/lib/yahooFinance";

function getArrow(netScore) {
  if (netScore >= 8) return '\u25B2\u25B2';
  if (netScore >= 1) return '\u25B2';
  if (netScore === 0) return '\u2014';
  if (netScore >= -7) return '\u25BC';
  return '\u25BC\u25BC';
}

function getColor(netScore) {
  if (netScore > 0) return '#22c55e';
  if (netScore < 0) return '#ef4444';
  return '#94a3b8';
}

export default function InstrumentStrip({ aggregation, onToggleFilter, activeFilters }) {
  const instrumentScores = aggregation?.instrument_scores || {};
  const [flashKeys, setFlashKeys] = useState(new Set());
  const [hoveredSymbol, setHoveredSymbol] = useState(null);
  const prevScores = useRef({});

  // Top 9 by absolute cumulative score
  const top9 = useMemo(() => {
    return Object.entries(instrumentScores)
      .sort((a, b) => Math.abs(b[1].net_score) - Math.abs(a[1].net_score))
      .slice(0, 9);
  }, [instrumentScores]);

  // Flash on score changes
  useEffect(() => {
    const newFlash = new Set();
    for (const [symbol, data] of Object.entries(instrumentScores)) {
      const prev = prevScores.current[symbol];
      if (prev && prev.net_score !== data.net_score) {
        newFlash.add(symbol);
      }
    }
    if (newFlash.size > 0) {
      setFlashKeys(newFlash);
      const timer = setTimeout(() => setFlashKeys(new Set()), 600);
      return () => clearTimeout(timer);
    }
    prevScores.current = { ...instrumentScores };
  }, [instrumentScores]);

  if (top9.length === 0) return null;

  return (
    <div
      data-testid="instrument-strip"
      className="mb-3 sm:mb-4 rounded-[10px] border"
      style={{ backgroundColor: '#161b22', borderColor: '#30363d' }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {top9.map(([symbol, data]) => {
          const color = getColor(data.net_score);
          const arrow = getArrow(data.net_score);
          const isFlashing = flashKeys.has(symbol);
          const isHovered = hoveredSymbol === symbol;
          const isFiltered = activeFilters?.includes(symbol);
          const yahooUrl = getYahooUrl(symbol);

          return (
            <a
              key={symbol}
              href={yahooUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`strip-instrument-${symbol}`}
              className="flex items-center justify-between px-3 py-2 transition-colors duration-100 no-underline"
              style={{
                borderBottom: '1px solid #21262d',
                borderRight: '1px solid #21262d',
                backgroundColor: isFiltered ? 'rgba(34, 197, 94, 0.06)' : 'transparent',
                animation: isFlashing ? 'cw-flash 600ms ease-out' : 'none',
                textDecoration: 'none',
              }}
              title={`View ${symbol} on Yahoo Finance | Shift+click to filter`}
              onClick={(e) => {
                if (e.shiftKey && onToggleFilter) {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleFilter("instruments", symbol);
                }
              }}
              onMouseEnter={() => setHoveredSymbol(symbol)}
              onMouseLeave={() => setHoveredSymbol(null)}
            >
              <span className="font-mono text-[13px] font-medium" style={{ color }}>
                {arrow} {symbol}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[13px] font-semibold" style={{ color }}>
                  {data.net_score > 0 ? '+' : ''}{data.net_score}
                </span>
                <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                  ({data.post_count}x)
                </span>
                {isHovered && (
                  <ExternalLink size={9} style={{ color, opacity: 0.5 }} />
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
